import { queryWithError } from 'hybridspace-graphql/lib/utils/queryWithError';
import { addDays, subDays } from 'owa-datetime';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

import { GetPlacesScheduleDocument } from '../graphql/__generated__/GetPlacesSchedule.interface';
import { getGqlDateTime } from '../utils/getGqlDateTime';
import { getDevErrorPageState } from 'places-diagnostics';
import { isAccountFeatureEnabled } from 'owa-feature-flags';

import type { DateRange } from 'owa-datetime-utils';
import type { GetPlacesScheduleQueryVariables } from '../graphql/__generated__/GetPlacesSchedule.interface';
import type {
    WorkLocationType,
    LocationIdType,
    LocationEntityType,
    LocationSourceType,
} from 'owa-graph-schema';
export default async function getScheduleWithLocation(
    smtpAddresses: string[],
    dateRange: DateRange
): Promise<
    | {
          __typename?: 'HybridspaceScheduleInformation';
          scheduleId: string;
          error?: {
              __typename?: 'CalendarDetailedError';
              message?: string | null;
              responseCode?: string | null;
              diagnosticData?: string | null;
          } | null;
          flexibleWorkingHours?: Array<{
              __typename?: 'HybridspaceGetScheduleFlexibleWorkingHoursView';
              workLocationType: WorkLocationType;
              isConfirmed: boolean;
              start: {
                  __typename?: 'ExtendedDateTime';
                  dateTime: string;
                  timeZone: {
                      __typename?: 'TimeZoneWH';
                      name: string;
                  };
              };
              end: {
                  __typename?: 'ExtendedDateTime';
                  dateTime: string;
                  timeZone: {
                      __typename?: 'TimeZoneWH';
                      name: string;
                  };
              };
              workLocationDetails?: {
                  __typename?: 'Location';
                  id?: string | null;
                  idType?: LocationIdType | null;
                  displayName?: string | null;
                  annotation?: string | null;
                  locationType?: LocationEntityType | null;
                  locationUri?: string | null;
                  locationSource?: LocationSourceType | null;
              } | null;
              building?: {
                  __typename?: 'Building';
                  id?: string | null;
                  name?: string | null;
                  campusId?: string | null;
                  address?: {
                      __typename?: 'PostalAddress';
                      country?: string | null;
                      postOfficeBox?: string | null;
                      postalCode?: string | null;
                      state?: string | null;
                      city?: string | null;
                      street?: string | null;
                      type?: string | null;
                  } | null;
              } | null;
          }> | null;
          flexibleWorkingHoursError?: {
              __typename?: 'CalendarDetailedError';
              message?: string | null;
              responseCode?: string | null;
              diagnosticData?: string | null;
          } | null;
      }[]
    | null
    | undefined
> {
    if (smtpAddresses.length === 0) {
        return [];
    }

    let scheduleWithLocation;
    try {
        // Manually throw an error to test error handling
        if (getDevErrorPageState() === 'Schedule') {
            throw new Error('could not get schedule');
        }
        const mailboxInfo = getModuleContextMailboxInfo();
        const scheduleVariables: GetPlacesScheduleQueryVariables | undefined = {
            input: {
                userId: mailboxInfo?.userIdentity ?? '',
                // The default interval in minutes.  Should not be less than 5 or greater than 1440.
                availabilityViewInterval: 30,
                startTime: getGqlDateTime(subDays(dateRange.start, 1)),
                endTime: getGqlDateTime(addDays(dateRange.end, 1)),
                schedules: [...smtpAddresses].sort(),
                mailboxInfo,
                viewType: isAccountFeatureEnabled('cal-location-sharing-dropdown', mailboxInfo)
                    ? 'WorkPlansOnly'
                    : undefined,
            },
            // Explicit cast to ignore missing mailboxInfo due to schema mismatch
        } as GetPlacesScheduleQueryVariables;

        const { data } = await queryWithError(GetPlacesScheduleDocument, {
            variables: scheduleVariables,
        });

        if (data?.getScheduleWithLocation) {
            scheduleWithLocation = data.getScheduleWithLocation.schedules;
        } else {
            throw new Error('PlacesGetScheduleWithLocation throw error');
        }
    } catch {
        throw new Error('PlacesGetScheduleWithLocation throw error');
    }
    return scheduleWithLocation;
}
